import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Briefcase, CheckCircle, Clock, DollarSign, Info, Send, Star, Upload } from 'lucide-react'
import { authApi } from '../lib/api/auth'
import Recaptcha, { type RecaptchaHandle } from '../components/Recaptcha'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

interface ApplicationForm {
  name: string
  email: string
  phone: string
  current_title: string
  role_expertise: string
  experience_years: string
  linkedin_url: string
  bio: string
}

const EXPERTISE_OPTIONS = [
  'Software Engineering',
  'Product Management',
  'Data Science & ML',
  'UX/UI Design',
  'Marketing & Growth',
  'Sales & Business Development',
  'Operations & Strategy',
  'Finance & Accounting',
  'Human Resources',
  'Other',
]

export default function InterviewerApply() {
  const [submitted, setSubmitted] = useState(false)
  const [error, setError]         = useState('')
  const [loading, setLoading]     = useState(false)
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [agreedToTerms, setAgreedToTerms]       = useState(false)
  const [termsError, setTermsError]             = useState('')
  const [agreedToSmsEmail, setAgreedToSmsEmail] = useState(false)

  // ── reCAPTCHA v2 (config-driven: rendered only when enabled by the backend) ──
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [captchaSiteKey, setCaptchaSiteKey] = useState('')
  const [captchaToken, setCaptchaToken]     = useState<string | null>(null)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  useEffect(() => {
    let active = true
    authApi
      .captchaConfig()
      .then((cfg) => {
        if (!active) return
        setCaptchaEnabled(cfg.enabled && !!cfg.site_key)
        setCaptchaSiteKey(cfg.site_key)
      })
      .catch(() => {
        // If the config can't be fetched, don't render the widget; the backend
        // still enforces verification when it is enabled.
        if (active) setCaptchaEnabled(false)
      })
    return () => { active = false }
  }, [])

  const [form, setForm] = useState<ApplicationForm>({
    name: '', email: '', phone: '',
    current_title: '', role_expertise: '', experience_years: '',
    linkedin_url: '', bio: '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Full Name validation
    const trimmedName = form.name.trim()
    if (trimmedName.length < 2) {
      setError('Full name must be at least 2 characters.')
      return
    }
    if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
      setError('Full name can only contain letters, spaces, hyphens, and apostrophes.')
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email.trim())) {
      setError('Please enter a valid email address.')
      return
    }

    // Phone validation (optional field) — normalize to E.164
    let e164Phone: string | null = null
    if (form.phone.trim()) {
      const parsed =
        parsePhoneNumberFromString(form.phone.trim()) ||
        parsePhoneNumberFromString(form.phone.trim(), 'US')
      if (!parsed || !parsed.isValid()) {
        setError('Please enter a valid phone number (e.g. +1 555 000 0000).')
        return
      }
      e164Phone = parsed.format('E.164')
    }

    // Years of experience validation
    const yearsVal = parseInt(form.experience_years, 10)
    if (isNaN(yearsVal) || yearsVal < 1 || yearsVal > 50) {
      setError('Years of experience must be between 1 and 50.')
      return
    }

    if (!agreedToTerms) {
      setTermsError('Please agree to the Terms of Service and Privacy Policy to continue.')
      return
    }

    if (captchaEnabled && !captchaToken) {
      setError('Please complete the CAPTCHA verification to continue.')
      return
    }

    setLoading(true)
    try {
      const payload = new FormData()
      payload.append('full_name', form.name)
      payload.append('email', form.email)
      if (e164Phone) payload.append('phone', e164Phone)
      payload.append('current_title', form.current_title)
      payload.append('specializations', form.role_expertise)
      payload.append('years_experience', String(parseInt(form.experience_years, 10)))
      if (form.linkedin_url) payload.append('linkedin_url', form.linkedin_url)
      payload.append('motivation', form.bio)
      payload.append('agreed_terms_policy', String(agreedToTerms))
      payload.append('agreed_sms_email_policy', String(agreedToSmsEmail))
      if (captchaToken) payload.append('recaptcha_token', captchaToken)
      if (resumeFile) payload.append('resume', resumeFile)
      await authApi.submitApplication(payload)
      setSubmitted(true)
    } catch (err: unknown) {
      // reCAPTCHA tokens are single-use — reset so the user can retry.
      if (captchaEnabled) {
        recaptchaRef.current?.reset()
        setCaptchaToken(null)
      }
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as any).response?.data?.message
          : err instanceof Error
          ? err.message
          : 'Failed to submit application. Please try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.successContainer}>
          <div style={styles.successCard}>
            <CheckCircle size={64} color="#22c55e" style={{ marginBottom: 24 }} />
            <h1 style={styles.successTitle}>Application Submitted!</h1>
            <p style={styles.successText}>
              Thank you for your interest in becoming an interviewer with Interview Dress Rehearsal.
              Our team will review your application and get back to you within 3–5 business days.
            </p>
            <Link to="/" className="btn btn-primary" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <ArrowLeft size={18} /> Back to Home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Link to="/" style={styles.backLink}>
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <img src="/InterviewDressRehearsal_LOGO.png" alt="IDR" style={styles.headerLogo} />
        </div>
      </header>

      {/* ── Body ── */}
      <div style={styles.container}>
        <div style={styles.layout} className="ia-layout">

          {/* ── Info column ── */}
          <div style={styles.infoCol}>
            <h1 style={styles.infoTitle}>Become an Interviewer</h1>
            <p style={styles.infoSubtitle}>
              Join our network of expert interviewers and help candidates prepare for their dream jobs.
            </p>

            <div style={styles.benefitsList}>
              {[
                { Icon: Clock,       title: 'Flexible Schedule',   desc: 'Set your own availability and work on your terms' },
                { Icon: DollarSign,  title: 'Competitive Pay',      desc: 'Tiered rates from $50–$100/hr based on expertise' },
                { Icon: Star,        title: 'Make an Impact',       desc: 'Help professionals land their dream roles' },
                { Icon: Briefcase,   title: 'Professional Growth',  desc: 'Sharpen your own skills while coaching others' },
              ].map(({ Icon, title, desc }) => (
                <div key={title} style={styles.benefitItem}>
                  <div style={styles.benefitIcon}><Icon size={20} /></div>
                  <div>
                    <h3 style={styles.benefitTitle}>{title}</h3>
                    <p style={styles.benefitDesc}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Form column ── */}
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>Application Form</h2>
            <p style={styles.formSubtitle}>Fill out the form below and our team will review your application.</p>

            {error && <div style={styles.errorBox}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input type="text" name="name" className="form-input" placeholder="Your full name" value={form.name} onChange={handleChange} required />
              </div>

              <div style={styles.formRow} className="ia-form-row">
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input type="email" name="email" className="form-input" placeholder="your@email.com" value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input type="tel" name="phone" className="form-input" placeholder="+1 (555) 000-0000" value={form.phone} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Current Job Title *</label>
                <input type="text" name="current_title" className="form-input" placeholder="e.g. Senior Software Engineer" value={form.current_title} onChange={handleChange} required />
              </div>

              <div style={styles.formRow} className="ia-form-row">
                <div className="form-group">
                  <label className="form-label">Area of Expertise *</label>
                  <select name="role_expertise" className="form-input" value={form.role_expertise} onChange={handleChange} required>
                    <option value="">Select your expertise…</option>
                    {EXPERTISE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Years of Experience *</label>
                  <input type="number" name="experience_years" className="form-input" placeholder="e.g. 8" min="1" max="50" value={form.experience_years} onChange={handleChange} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">LinkedIn Profile URL</label>
                <input type="url" name="linkedin_url" className="form-input" placeholder="https://linkedin.com/in/yourprofile" value={form.linkedin_url} onChange={handleChange} />
              </div>

              {/* Resume upload */}
              <div className="form-group">
                <label className="form-label">Upload Resume</label>
                <div
                  style={styles.uploadArea}
                  onClick={() => document.getElementById('resume-input')?.click()}
                >
                  <input
                    id="resume-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)}
                  />
                  {resumeFile ? (
                    <div style={styles.uploadSelected}>
                      <CheckCircle size={20} color="#22c55e" />
                      <span style={styles.uploadFilename}>{resumeFile.name}</span>
                      <button
                        type="button"
                        style={styles.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation()
                          setResumeFile(null)
                          const input = document.getElementById('resume-input') as HTMLInputElement | null
                          if (input) input.value = ''
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div style={styles.uploadPlaceholder}>
                      <Upload size={24} color="#9ca3af" />
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Click to upload your resume</span>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>PDF, DOC, or DOCX (max 5MB)</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tell us about yourself *</label>
                <textarea
                  name="bio"
                  className="form-input"
                  placeholder="Share your professional background, interviewing experience, and why you want to join our team…"
                  rows={5}
                  value={form.bio}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Agreements */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => { setAgreedToTerms(e.target.checked); if (e.target.checked) setTermsError('') }}
                  />
                  <span>
                    I agree to the{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>
                  </span>
                </label>
                {termsError && (
                  <p style={{ color: '#d97706', fontSize: 13, margin: '2px 0 0 24px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={13} /> {termsError}
                  </p>
                )}

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToSmsEmail}
                    onChange={(e) => setAgreedToSmsEmail(e.target.checked)}
                  />
                  <span>I consent to receive SMS notifications about bookings, reminders, and account updates. Message and data rates may apply.</span>
                </label>
              </div>

              {captchaEnabled && captchaSiteKey && (
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
                  <Recaptcha
                    ref={recaptchaRef}
                    siteKey={captchaSiteKey}
                    onChange={(token) => {
                      setCaptchaToken(token)
                      if (token) setError('')
                    }}
                    onError={() => setCaptchaToken(null)}
                  />
                  <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Info size={13} /> Please complete the CAPTCHA verification to continue.
                  </p>
                </div>
              )}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px', fontSize: 15 }} disabled={loading}>
                {loading ? 'Submitting…' : <><Send size={18} /> Submit Application</>}
              </button>
            </form>
          </div>

        </div>
      </div>

      <style>{`
        .ia-layout {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 48px;
          align-items: start;
        }
        .ia-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 900px) {
          .ia-layout { grid-template-columns: 1fr; gap: 32px; }
        }
        @media (max-width: 600px) {
          .ia-form-row { grid-template-columns: 1fr; gap: 0; }
        }
      `}</style>
    </div>
  )
}

// ── Inline styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#faf8f0',
    fontFamily: 'Inter, -apple-system, sans-serif',
  },
  header: {
    background: 'white',
    borderBottom: '1px solid #e2e0d5',
    padding: '16px 0',
  },
  headerInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLogo: { width: 40, height: 40, borderRadius: '50%' },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: '#2d3748',
    fontWeight: 500,
    fontSize: 14,
    textDecoration: 'none',
  },
  container: { maxWidth: 1200, margin: '0 auto', padding: '48px 24px' },
  layout: {
    gap: 48,
    alignItems: 'start',
  },
  infoCol: {},
  infoTitle: { fontSize: 36, fontWeight: 700, color: '#1e2432', marginBottom: 12, lineHeight: 1.2 },
  infoSubtitle: { fontSize: 17, color: '#64748b', lineHeight: 1.6, marginBottom: 40 },
  benefitsList: { display: 'flex', flexDirection: 'column', gap: 24 },
  benefitItem: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  benefitIcon: {
    width: 44, height: 44,
    borderRadius: 12,
    background: '#2d3748',
    color: '#f5f0dc',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  benefitTitle: { fontSize: 15, fontWeight: 600, color: '#1e2432', marginBottom: 4 },
  benefitDesc:  { fontSize: 14, color: '#64748b', lineHeight: 1.4 },
  formCard: {
    background: 'white',
    borderRadius: 16,
    padding: 36,
    border: '1px solid #e2e0d5',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  formTitle:    { fontSize: 22, fontWeight: 700, color: '#1e2432', marginBottom: 4 },
  formSubtitle: { fontSize: 14, color: '#64748b', marginBottom: 24 },
  errorBox: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 20,
    border: '1px solid #fecaca',
  },
  formRow: { gap: 16 },
  uploadArea: {
    border: '2px dashed #d1d5db',
    borderRadius: 10,
    padding: 24,
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  uploadPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  uploadSelected: { display: 'flex', alignItems: 'center', gap: 10 },
  uploadFilename: { fontSize: 14, color: '#374151', flex: 1, textAlign: 'left' as const },
  removeBtn: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '4px 12px',
    fontSize: 12,
    color: '#dc2626',
    cursor: 'pointer',
    fontWeight: 500,
  },
  successContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  successCard: {
    maxWidth: 560,
    background: 'white',
    borderRadius: 16,
    padding: 48,
    textAlign: 'center' as const,
    border: '1px solid #e2e0d5',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  successTitle: { fontSize: 28, fontWeight: 700, color: '#1e2432', marginBottom: 12 },
  successText:  { fontSize: 15, color: '#64748b', lineHeight: 1.6, marginBottom: 32 },
}
